/**
 * @file platformService.ts
 * @description Frontend service and React Query hooks for Platform Health dashboard
 * @feature 141-platform-self-monitoring
 * @constitution v1.12.0
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../apiClient';
import type {
  PlatformSummary,
  ContainerApp,
  ContainerRegistry,
  LogicApp,
} from '../../types/platform';
import { formatEasternDate } from '@/utils/dateFormatter';

const API_BASE = '/api/operations/platform';

// ============================================================================
// API Functions (using apiClient for auth token injection)
// ============================================================================

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await apiClient.get<{ success: boolean; data: T; error?: string }>(
    `${API_BASE}${endpoint}`
  );
  if (!response.data.success) {
    throw new Error(response.data.error || 'API returned unsuccessful response');
  }
  return response.data.data;
}

// ============================================================================
// T014: usePlatformSummary
// ============================================================================

export function usePlatformSummary() {
  return useQuery<PlatformSummary>({
    queryKey: ['platform', 'summary'],
    queryFn: () => fetchApi<PlatformSummary>('/summary'),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute auto-refresh
  });
}

// ============================================================================
// T013: useContainers
// ============================================================================

export function useContainers() {
  return useQuery<ContainerApp[]>({
    queryKey: ['platform', 'containers'],
    queryFn: () => fetchApi<ContainerApp[]>('/containers'),
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// T015: useContainerHistory
// ============================================================================

export function useContainerHistory(name: string | null) {
  return useQuery<ContainerApp[]>({
    queryKey: ['platform', 'containers', name, 'history'],
    queryFn: () => fetchApi<ContainerApp[]>(`/containers/${encodeURIComponent(name!)}/history`),
    enabled: !!name,
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// T022: useRegistry
// ============================================================================

export function useRegistry() {
  return useQuery<ContainerRegistry[]>({
    queryKey: ['platform', 'registry'],
    queryFn: () => fetchApi<ContainerRegistry[]>('/registry'),
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// T026: useLogicApps
// ============================================================================

export function useLogicApps() {
  return useQuery<LogicApp[]>({
    queryKey: ['platform', 'logic-apps'],
    queryFn: () => fetchApi<LogicApp[]>('/logic-apps'),
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// T027: useLogicAppHistory
// ============================================================================

export function useLogicAppHistory(name: string | null) {
  return useQuery<LogicApp[]>({
    queryKey: ['platform', 'logic-apps', name, 'history'],
    queryFn: () => fetchApi<LogicApp[]>(`/logic-apps/${encodeURIComponent(name!)}/history`),
    enabled: !!name,
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// Spec 216 T022/T023: DB-only job-status + reachability read models
// ============================================================================

export type TileSignal = 'ok' | 'attention' | 'stale' | 'not-collected';

export interface JobStatusView {
  job_name: string;
  environment: string | null;
  last_execution_name: string | null;
  last_execution_status: string | null;
  last_execution_start: string | null;
  last_execution_end: string | null;
  execution_count_24h: number | null;
  failure_count_24h: number | null;
  expected_schedule: string | null;
  actual_drift_seconds: number | null;
  signal: TileSignal;
  criterion: string;
  lastCollectedAt: string;
}

export interface ReachabilityComponent {
  component: string;
  source: 'heartbeat' | 'endpoint-probe';
  signal: TileSignal;
  criterion: string;
  lastCollectedAt: string | null;
  latencyMs: number | null;
  detail: string | null;
}

export function usePlatformJobs() {
  return useQuery<JobStatusView[]>({
    queryKey: ['platform', 'jobs'],
    queryFn: () => fetchApi<JobStatusView[]>('/jobs'),
    staleTime: 30 * 1000,
  });
}

export function usePlatformReachability() {
  return useQuery<ReachabilityComponent[]>({
    queryKey: ['platform', 'reachability'],
    queryFn: () => fetchApi<ReachabilityComponent[]>('/reachability'),
    staleTime: 30 * 1000,
  });
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Format date to relative time (e.g., "2h ago")
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatEasternDate(date);
}

/**
 * Get container status badge color
 */
export function getContainerStatusColor(
  provisioningState: string | null,
  runningStatus: string | null,
  lastExecutionStatus: string | null
): 'green' | 'red' | 'gray' | 'yellow' {
  if (provisioningState === 'Failed' || lastExecutionStatus === 'Failed') return 'red';
  if (runningStatus === 'Running') return 'green';
  if (runningStatus === 'Stopped') return 'gray';
  if (lastExecutionStatus === 'Succeeded') return 'green';
  if (provisioningState === 'Succeeded') return 'green';
  // Resource exists in Azure but has no runtime data — show as provisioned (green-ish)
  if (!provisioningState && !runningStatus && !lastExecutionStatus) return 'gray';
  return 'yellow';
}

/**
 * Get container status label
 */
export function getContainerStatusLabel(
  provisioningState: string | null,
  runningStatus: string | null,
  lastExecutionStatus: string | null,
  resourceType: string
): string {
  // For jobs, use last execution status
  if (resourceType === 'jobs') {
    if (lastExecutionStatus === 'Failed') return 'Failed';
    if (lastExecutionStatus === 'Succeeded') return 'Succeeded';
    if (lastExecutionStatus) return lastExecutionStatus;
    // No execution data available — it exists in Azure
    return 'Registered';
  }
  // For apps, use running status
  if (runningStatus) return runningStatus;
  if (provisioningState === 'Failed') return 'Failed';
  if (provisioningState === 'Succeeded') return 'Running';
  if (provisioningState) return provisioningState;
  // No status data — resource exists in inventory
  return 'Registered';
}
